// ======================================================================
// FILE:
// src/modules/finance/transactions/manual_journal/components/JournalTotals.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Manual Journal - Create Journal
//
// PURPOSE:
// Display journal summary information and accounting totals.
//
// RESPONSIBILITIES:
// - Display total journal lines
// - Display journal narration
// - Display attachment count
// - Display total debit
// - Display total credit
// - Display difference
// - Clearly indicate balanced / unbalanced journal
//
// DOES NOT HANDLE:
// - Journal creation
// - Journal line API
// - Save Draft
// - Submit for Approval
// - Navigation
// - Toast / notification handling
// - Global state management
//
// IMPORTANT:
// - Totals are calculated from the current journal lines.
// - Backend remains authoritative for final accounting validation.
// - Submit must ultimately be blocked when Debit != Credit.
// ======================================================================

import React from "react";

// ======================================================================
// COMPONENT
// ======================================================================

export default function JournalTotals({

    lines = [],

    narration = "",

    attachmentCount = 0,

}) {

    // ==================================================================
    // TOTAL DEBIT
    // ==================================================================

    const totalDebit =
        lines.reduce(
            (
                total,
                line
            ) => {

                return (
                    total +
                    (
                        Number(
                            line?.debit_amount
                        ) || 0
                    )
                );

            },
            0
        );

    // ==================================================================
    // TOTAL CREDIT
    // ==================================================================

    const totalCredit =
        lines.reduce(
            (
                total,
                line
            ) => {

                return (
                    total +
                    (
                        Number(
                            line?.credit_amount
                        ) || 0
                    )
                );

            },
            0
        );

    // ==================================================================
    // DIFFERENCE
    // ==================================================================

    const difference =
        totalDebit -
        totalCredit;

    // ==================================================================
    // BALANCED
    // ==================================================================

    const isBalanced =
        Math.abs(
            difference
        ) < 0.000001;

    // ==================================================================
    // FORMAT AMOUNT
    // ==================================================================

    const formatAmount = (
        amount
    ) => {

        return Number(
            amount || 0
        ).toLocaleString(
            "en-IN",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }
        );

    };

    // ==================================================================
    // RENDER
    // ==================================================================

    return (

        <div className="card shadow-sm border-0 mb-4">

            {/* ==========================================================
                SECTION HEADER
            ========================================================== */}

            <div className="card-header bg-white border-bottom">

                <div className="d-flex align-items-center">

                    <div
                        className="
                            rounded-circle
                            bg-primary
                            text-white
                            d-flex
                            align-items-center
                            justify-content-center
                            me-2
                        "
                        style={{
                            width: 30,
                            height: 30,
                            fontSize: 14,
                            fontWeight: 600,
                        }}
                    >
                        3
                    </div>

                    <span className="fw-semibold">
                        Summary &amp; Totals
                    </span>

                </div>

            </div>

            {/* ==========================================================
                BODY
            ========================================================== */}

            <div className="card-body">

                <div className="row g-4">

                    {/* ==================================================
                        SUMMARY
                    ================================================== */}

                    <div className="col-lg-7 col-md-12">

                        <div
                            className="
                                border
                                rounded
                                p-3
                                h-100
                            "
                        >

                            <div className="row g-3">

                                {/* ======================================
                                    TOTAL LINES
                                ====================================== */}

                                <div className="col-sm-4">

                                    <div className="text-muted small">
                                        Total Lines
                                    </div>

                                    <div className="fw-semibold fs-5">
                                        {lines.length}
                                    </div>

                                </div>

                                {/* ======================================
                                    NARRATION
                                ====================================== */}

                                <div className="col-sm-8">

                                    <div className="text-muted small">
                                        Narration
                                    </div>

                                    <div
                                        className="
                                            fw-semibold
                                            text-break
                                        "
                                        style={{
                                            wordBreak:
                                                "break-word",
                                        }}
                                    >
                                        {narration ||
                                            "-"}
                                    </div>

                                </div>

                                {/* ======================================
                                    ATTACHMENTS
                                ====================================== */}

                                <div className="col-sm-4">

                                    <div className="text-muted small">
                                        Attachments
                                    </div>

                                    <div className="fw-semibold">
                                        {attachmentCount}
                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                    {/* ==================================================
                        TOTALS
                    ================================================== */}

                    <div className="col-lg-5 col-md-12">

                        <div
                            className="
                                border
                                rounded
                                overflow-hidden
                            "
                        >

                            {/* ==========================================
                                TOTAL DEBIT
                            ========================================== */}

                            <div
                                className="
                                    d-flex
                                    justify-content-between
                                    align-items-center
                                    px-3
                                    py-2
                                    border-bottom
                                "
                            >

                                <span className="text-muted">
                                    Total Debit
                                </span>

                                <span className="fw-semibold">
                                    ₹{" "}
                                    {formatAmount(
                                        totalDebit
                                    )}
                                </span>

                            </div>

                            {/* ==========================================
                                TOTAL CREDIT
                            ========================================== */}

                            <div
                                className="
                                    d-flex
                                    justify-content-between
                                    align-items-center
                                    px-3
                                    py-2
                                    border-bottom
                                "
                            >

                                <span className="text-muted">
                                    Total Credit
                                </span>

                                <span className="fw-semibold">
                                    ₹{" "}
                                    {formatAmount(
                                        totalCredit
                                    )}
                                </span>

                            </div>

                            {/* ==========================================
                                DIFFERENCE
                            ========================================== */}

                            <div
                                className={`
                                    d-flex
                                    justify-content-between
                                    align-items-center
                                    px-3
                                    py-2
                                    ${
                                        isBalanced
                                            ? "bg-success-subtle"
                                            : "bg-danger-subtle"
                                    }
                                `}
                            >

                                <span
                                    className={
                                        isBalanced
                                            ? "text-success fw-semibold"
                                            : "text-danger fw-semibold"
                                    }
                                >
                                    Difference
                                </span>

                                <span
                                    className={
                                        isBalanced
                                            ? "text-success fw-semibold"
                                            : "text-danger fw-semibold"
                                    }
                                >
                                    ₹{" "}
                                    {formatAmount(
                                        Math.abs(
                                            difference
                                        )
                                    )}
                                </span>

                            </div>

                        </div>

                    </div>

                </div>

                {/* ======================================================
                    BALANCE MESSAGE
                ====================================================== */}

                <div
                    className={`
                        mt-3
                        small
                        ${
                            isBalanced
                                ? "text-success"
                                : "text-danger"
                        }
                    `}
                >

                    {isBalanced ? (

                        <>
                            <i className="fas fa-check-circle me-1" />
                            Journal is balanced.
                        </>

                    ) : (

                        <>
                            <i className="fas fa-exclamation-circle me-1" />
                            Journal is out of balance by ₹{" "}
                            {formatAmount(
                                Math.abs(
                                    difference
                                )
                            )}
                            .
                        </>

                    )}

                </div>

            </div>

        </div>
    );
}